import { PinCandidate } from '../../perps-v1-shared/types';
import { PreviewRow } from '../dataProvider';

// Merges the confirmed fetch (`rows`) with the in-progress draft (`manualSymbols`) into the rows
// ViewPreviewPanel's table renders, applying the SAME precedence the server uses to classify a
// symbol's source — pinned > manual > rule — see `classifyPreviewSource` in hypertrader's
// internal/services/marketlist/preview.go. That precedence is the server's, not invented here; a
// symbol the server already returned as `pinned` MUST NOT also get a synthesised manual row, or it
// renders twice (task DUPPREVIEW).
//
// Rule-tier finding (DUPPREVIEW step 1): `rows` can be stale relative to `manualSymbols` — e.g. the
// admin just added a symbol and the confirming fetch hasn't landed yet — so a symbol now in the
// draft's include list can still be classified `rule` in `rows`. Per the server's precedence, manual
// outranks rule, so such a symbol is promoted into the manual block here (reusing its real market
// data from the `rule` row rather than resetting it to the optimistic "—" placeholder) and dropped
// from `ruleRows` so it does not also render there.
export function buildDisplayRows(
  rows: PreviewRow[],
  manualSymbols: string[],
  candidateMap: Map<string, PinCandidate>
): PreviewRow[] {
  const pinnedRows = rows.filter((r) => r.source === 'pinned');
  const pinnedSet = new Set(pinnedRows.map((r) => r.symbol));
  const manualSymbolSet = new Set(manualSymbols);
  const confirmedBySymbol = new Map(rows.map((r) => [r.symbol, r]));

  const manualRows: PreviewRow[] = manualSymbols
    // pinned wins over manual — never re-add a symbol the server already placed in pinnedRows.
    .filter((symbol) => !pinnedSet.has(symbol))
    .map((symbol) => {
      const confirmed = confirmedBySymbol.get(symbol);
      if (confirmed) {
        // Already known to the server, as either `manual` or (stale) `rule` — either way it's
        // manual now that it's in the draft's include list. Real data, source normalised.
        return confirmed.source === 'manual' ? confirmed : { ...confirmed, source: 'manual' as const };
      }
      // Not yet confirmed by any fetch — optimistic placeholder so the panel gives instant
      // feedback instead of waiting on the network (see decision D13 / ViewPreviewPanel's doc
      // comment).
      const candidate = candidateMap.get(symbol);
      return {
        id: symbol,
        symbol,
        displaySymbol: candidate?.displaySymbol ?? symbol,
        dex: candidate?.dex ?? '',
        source: 'manual' as const,
        markPx: '',
        changePercent24h: '',
        volume24h: '',
        marketCap: '',
        listedAt: '',
      };
    });

  // Symbols promoted into manualRows above must not also linger here under their stale `rule`
  // classification.
  const ruleRows = rows.filter((r) => r.source === 'rule' && !manualSymbolSet.has(r.symbol));

  // Manual entries appear after any pinned entries (see membersIncludeHint) and before rule
  // matches — matches the server's own ordering, just with the manual block's contents/order
  // driven by the live draft instead of the (possibly stale) last confirmed fetch.
  return [...pinnedRows, ...manualRows, ...ruleRows];
}

// A row's `id` as returned by the server is `symbol` — see cause 2 of task DUPPREVIEW: a pinned
// row and a synthesised manual row for the same symbol shared that id as their React `key`, so
// React reused DOM nodes across them and produced the impossible "1,2,3,2,3,4,5" row numbering.
// Deriving the key from BOTH source and symbol means even a future classification bug (two
// buckets disagreeing about one symbol) degrades into two visibly-separate rows instead of
// corrupted/merged DOM state, while staying stable across re-renders for the same logical row
// (same source + same symbol) — required so MUI doesn't lose scroll position or input focus.
export function displayRowKey(row: PreviewRow): string {
  return `${row.source}:${row.symbol}`;
}
